import React from "react";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  BarChart,
  GetApp,
  FileDownload,
  Search,
  Settings,
} from "@mui/icons-material";
import {
  SheetData,
  BatchAdvancedStats,
  Brand,
  Batch,
  User,
} from "../../../pages/playground/types";
import { exportTimeTracking, exportBatch } from "../../../api/batches";
import { JobStatus, ClientJobStatus } from "../../../pages/playground/types";
import { downloadHelper } from "../../../utils/download";

export interface JobListActionMenuProps {
  brand?: Brand;
  batch?: Batch;
  user?: User;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  sheet: SheetData | null;
  advancedStats: BatchAdvancedStats | null;
  isCompleted: boolean;
  isPolling: boolean;
  onOpenAdvancedStats: () => void;
  onOpenExport: () => void;
  showKeywordSearch: boolean;
  onToggleKeywordSearch: () => void;
  onResetupBatch?: () => void;
  canSeeBatchConfiguration?: boolean;
}

/**
 * Secondary actions menu (show/hide columns, stats, exports)
 */
export const JobListActionMenu: React.FC<JobListActionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  advancedStats,
  isCompleted,
  isPolling,
  onOpenAdvancedStats,
  onOpenExport,
  user,
  brand,
  batch,
  onToggleKeywordSearch,
  onResetupBatch,
  canSeeBatchConfiguration,
}) => {
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {!user?.isCopywriter && isCompleted && (
        <MenuItem
          onClick={() => {
            onToggleKeywordSearch();
            onClose();
          }}
        >
          <ListItemIcon>
            <Search fontSize="small" />
          </ListItemIcon>
          <ListItemText>Keywords Search</ListItemText>
        </MenuItem>
      )}

      {!user?.isClientRelated && advancedStats && isCompleted && (
        <MenuItem
          onClick={() => {
            onOpenAdvancedStats();
            onClose();
          }}
          disabled={isPolling}
        >
          <ListItemIcon>
            <BarChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Statistics</ListItemText>
        </MenuItem>
      )}

      {isCompleted && user?.isClientRelated && (
        <>
          <MenuItem
            onClick={async () => {
              const hasLaraLanguages =
                (batch?.settings?.lara_languages?.length ?? 0) > 0;
              const allStatuses = Object.values(ClientJobStatus).filter(
                (status) => status === ClientJobStatus.COMPLETED,
              ) as unknown as JobStatus[];
              await downloadHelper(
                () =>
                  exportBatch({
                    batchId: String(batch?.id),
                    statuses: allStatuses,
                    columns: hasLaraLanguages ? ["translations"] : [],
                    format: "csv",
                  }),
                {
                  fallbackFilename: `${brand?.name}_${batch?.name}_export`,
                  format: "csv",
                },
              );
              onClose();
            }}
          >
            <ListItemIcon>
              <GetApp fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export CSV</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={async () => {
              const hasLaraLanguages =
                (batch?.settings?.lara_languages?.length ?? 0) > 0;
              const allStatuses = Object.values(ClientJobStatus).filter(
                (status) => status === ClientJobStatus.COMPLETED,
              ) as unknown as JobStatus[];
              await downloadHelper(
                () =>
                  exportBatch({
                    batchId: String(batch?.id),
                    statuses: allStatuses,
                    columns: hasLaraLanguages ? ["translations"] : [],
                    format: "xlsx",
                  }),
                {
                  fallbackFilename: `${brand?.name}_${batch?.name}_export`,
                  format: "xlsx",
                },
              );
              onClose();
            }}
          >
            <ListItemIcon>
              <GetApp fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export Excel</ListItemText>
          </MenuItem>
        </>
      )}

      {isCompleted && !user?.isClientRelated && (
        <MenuItem
          onClick={() => {
            onOpenExport();
            onClose();
          }}
        >
          <ListItemIcon>
            <GetApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
      )}

      {user?.isAdmin && (
        <MenuItem
          onClick={async () => {
            await downloadHelper(() =>
              exportTimeTracking({
                brandId: String(brand?.id),
                batchId: String(batch?.id),
              }),
            );
            onClose();
          }}
        >
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Time Tracking</ListItemText>
        </MenuItem>
      )}

      {canSeeBatchConfiguration &&
        batch?.batch_configuration_completed &&
        onResetupBatch && (
          <MenuItem
            onClick={() => {
              onResetupBatch();
              onClose();
            }}
          >
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Re-setup Batch</ListItemText>
          </MenuItem>
        )}
    </Menu>
  );
};
